import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon } from "@/components/ui/icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import { Input, InputField } from "@/components/ui/input";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { Check } from "lucide-react-native";
import {
  getOrganizationControllerListQueryKey,
  getUserPartControllerMyParticipantsQueryKey,
} from "@/generated/api/api";
import { useMemo, useState } from "react";
import { Link as ExpoLink, useRouter } from "expo-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  useJoinByCodeControllerFindOrganization,
  useJoinByCodeControllerJoinByCode,
} from "@/generated/api/api";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { useToast } from "@/utils/useToast";

export function JoinByCodeModal(
  props: Pick<InterfaceModalProps, "isOpen" | "onClose">,
) {
  const [code, setCode] = useState("");
  const normalizedCode = useMemo(
    () =>
      code
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 6),
    [code],
  );

  const {
    data: organization,
    isLoading: isValidating,
    error,
  } = useJoinByCodeControllerFindOrganization(normalizedCode, {
    query: {
      enabled: normalizedCode.length === 6,
      retry: false,
      meta: {
        noSystemError: true,
      },
    },
  });

  const { mutate: joinByCode, isPending: isJoining } =
    useJoinByCodeControllerJoinByCode();

  const queryClient = useQueryClient();
  const router = useRouter();
  const { showToast } = useToast();

  const onClose = () => {
    setCode("");
    props.onClose?.();
  };

  const onJoin = () => {
    joinByCode(
      { code: normalizedCode },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getUserPartControllerMyParticipantsQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getOrganizationControllerListQueryKey(),
          });
          showToast("success", `Successfully joined ${organization?.name}!`);
          onClose();
        },
        onError: () => {
          showToast("error", "Failed to join team. Please try again.");
        },
      },
    );
  };

  const isCodeValid = normalizedCode.length === 6 && organization && !error;
  const showError = normalizedCode.length === 6 && !!error;

  return (
    <Modal isOpen={props.isOpen} onClose={onClose} size="md" avoidKeyboard>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Join a team
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>Enter the 6-character invitation code you received:</Text>
            <Input size="xl" variant="outline" className="h-16">
              <InputField
                value={normalizedCode}
                onChangeText={setCode}
                placeholder="ABC123"
                autoCapitalize="characters"
                maxLength={6}
                className="text-center text-2xl font-bold tracking-widest"
              />
            </Input>
            {isValidating && (
              <Text size="sm" className="text-typography-500 text-center">
                Validating code...
              </Text>
            )}
            {showError && (
              <Text size="sm" className="text-error-500 text-center">
                Invalid invitation code. Please check and try again.
              </Text>
            )}
            {isCodeValid && organization && (
              <VStack className="gap-2 bg-background-50 p-4 rounded-lg">
                <Text size="sm" className="text-typography-500">
                  You're joining:
                </Text>
                <Text bold size="lg">
                  {organization.name}
                </Text>
                {organization.address && (
                  <Text size="sm" className="text-typography-500">
                    {organization.address}
                  </Text>
                )}
              </VStack>
            )}
            {isCodeValid && (
              <VStack className="gap-1">
                <Text size="sm">
                  By clicking "Join" you agree to participate in their
                  tips-sharing scheme.
                </Text>
                <Text size="sm">
                  Learn more in our{" "}
                  <ExpoLink
                    href="https://gotips.co/client-terms/"
                    target="_blank"
                  >
                    <Text size="sm" bold>
                      Terms of Use
                    </Text>
                  </ExpoLink>
                </Text>
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" action="secondary" onPress={onClose}>
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button onPress={onJoin} isDisabled={!isCodeValid || isJoining}>
            <ButtonIcon as={Check} />
            <ButtonText>Join team</ButtonText>
            {isJoining && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
